import type { ChartDatum } from "../../types/demo";

// --- Shared utilities ---

const formatValue = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return value.toLocaleString();
};

const formatFullValue = (value: number): string => {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

/** Compute a nice step size for a given range span. */
const niceStep = (range: number, targetTicks: number): number => {
  if (range <= 0) return 1;
  const rough = range / Math.max(targetTicks - 1, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const residual = rough / magnitude;
  const nice = residual <= 1.5 ? 1 : residual <= 3 ? 2 : residual <= 7 ? 5 : 10;
  return nice * magnitude;
};

/** Generate tick values that cover [dataMin, dataMax] with nice intervals. */
const niceRange = (dataMin: number, dataMax: number, targetTicks = 5): { ticks: number[]; floor: number; ceiling: number } => {
  // All zeros or single value
  if (dataMin >= dataMax) {
    const pad = dataMin === 0 ? 1 : Math.abs(dataMin) * 0.1;
    return niceRange(dataMin - pad, dataMax + pad, targetTicks);
  }

  const step = niceStep(dataMax - dataMin, targetTicks);
  const floor = Math.floor(dataMin / step) * step;
  const ceiling = Math.ceil(dataMax / step) * step;

  const ticks: number[] = [];
  for (let v = floor; v <= ceiling + step * 0.001; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6); // avoid float drift
  }

  return { ticks, floor: ticks[0] ?? floor, ceiling: ticks[ticks.length - 1] ?? ceiling };
};

// --- Colours ---

const BLUE_500 = "#3b82f6";
const BLUE_400 = "#60a5fa";
const BLUE_300 = "#93c5fd";
const RED_400 = "#f87171";

// --- Line Chart ---

export const LineChart = ({ data }: { data: ChartDatum[] }) => {
  if (data.length === 0) return null;

  const values = data.map(d => d.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const { ticks, floor, ceiling } = niceRange(dataMin, dataMax);
  const range = ceiling - floor || 1;

  const left = 54;
  const right = 16;
  const top = 16;
  const bottom = 34;
  const svgWidth = 560;
  const svgHeight = 200;
  const plotW = svgWidth - left - right;
  const plotH = svgHeight - top - bottom;

  const toX = (i: number) => left + (i / Math.max(data.length - 1, 1)) * plotW;
  const toY = (v: number) => top + plotH - ((v - floor) / range) * plotH;

  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.value).toFixed(1)}`).join(" ");

  // Area fill — fill down to the zero line (or floor if all same sign)
  const zeroY = floor >= 0 ? toY(floor) : ceiling <= 0 ? toY(ceiling) : toY(0);
  const areaPath = `${linePath} L${toX(data.length - 1).toFixed(1)},${zeroY.toFixed(1)} L${toX(0).toFixed(1)},${zeroY.toFixed(1)} Z`;

  const uid = `lc-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full" style={{ aspectRatio: `${svgWidth}/${svgHeight}` }}>
        <defs>
          <linearGradient id={`${uid}-stroke`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={BLUE_500} />
            <stop offset="100%" stopColor={BLUE_300} />
          </linearGradient>
          <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE_500} stopOpacity="0.15" />
            <stop offset="100%" stopColor={BLUE_500} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines + labels */}
        {ticks.map(tick => {
          const y = toY(tick);
          const isZero = tick === 0 && floor < 0;
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={svgWidth - right} y2={y} stroke={isZero ? "#475569" : "#334155"} strokeOpacity={isZero ? 0.35 : 0.12} strokeWidth="1" />
              <text x={left - 8} y={y + 1} textAnchor="end" dominantBaseline="middle" fill="#94a3b8" style={{ fontSize: "11px", fontWeight: 500, fontFamily: "system-ui" }}>
                {formatValue(tick)}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#${uid}-fill)`} />

        {/* Line */}
        <path d={linePath} fill="none" stroke={`url(#${uid}-stroke)`} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Data points */}
        {data.map((d, i) => (
          <circle key={i} cx={toX(i)} cy={toY(d.value)} r="3" fill="white" stroke={BLUE_500} strokeWidth="2" />
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const showEvery = data.length > 10 ? 2 : 1;
          if (i % showEvery !== 0 && i !== data.length - 1) return null;
          return (
            <text key={i} x={toX(i)} y={svgHeight - 6} textAnchor="middle" fill="#94a3b8" style={{ fontSize: "11px", fontWeight: 500, fontFamily: "system-ui" }}>
              {d.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// --- Bar Chart (vertical) ---

export const BarChart = ({ data }: { data: ChartDatum[] }) => {
  if (data.length === 0) return null;

  const values = data.map(d => d.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  // Always include 0 in bar chart range so bars anchor from zero
  const { ticks, floor, ceiling } = niceRange(Math.min(dataMin, 0), Math.max(dataMax, 0));
  const range = ceiling - floor || 1;

  const left = 54;
  const right = 16;
  const top = 24;
  const bottom = 44;
  const svgWidth = 560;
  const svgHeight = 220;
  const plotW = svgWidth - left - right;
  const plotH = svgHeight - top - bottom;

  const slotWidth = plotW / data.length;
  const barWidth = slotWidth * (data.length <= 4 ? 0.55 : 0.65);
  const toY = (v: number) => top + plotH - ((v - floor) / range) * plotH;
  const zeroY = toY(0);

  const uid = `bc-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full" style={{ aspectRatio: `${svgWidth}/${svgHeight}` }}>
        <defs>
          <linearGradient id={`${uid}-bar-pos`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE_400} />
            <stop offset="100%" stopColor={BLUE_500} />
          </linearGradient>
          <linearGradient id={`${uid}-bar-neg`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RED_400} />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines + labels */}
        {ticks.map(tick => {
          const y = toY(tick);
          const isZero = tick === 0 && floor < 0;
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={svgWidth - right} y2={y} stroke={isZero ? "#475569" : "#334155"} strokeOpacity={isZero ? 0.4 : 0.12} strokeWidth={isZero ? 1.5 : 1} />
              <text x={left - 8} y={y + 1} textAnchor="end" dominantBaseline="middle" fill="#94a3b8" style={{ fontSize: "11px", fontWeight: 500, fontFamily: "system-ui" }}>
                {formatValue(tick)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = left + i * slotWidth + (slotWidth - barWidth) / 2;
          const isNeg = d.value < 0;
          const barTop = isNeg ? zeroY : toY(d.value);
          const barBottom = isNeg ? toY(d.value) : zeroY;
          const barH = Math.max(barBottom - barTop, 0.5);
          return (
            <g key={i}>
              <rect x={x} y={barTop} width={barWidth} height={barH} rx={3} fill={`url(#${uid}-bar-${isNeg ? "neg" : "pos"})`} />
              {/* Value label */}
              <text
                x={x + barWidth / 2}
                y={isNeg ? barBottom + 14 : barTop - 7}
                textAnchor="middle"
                fill={isNeg ? RED_400 : "#cbd5e1"}
                style={{ fontSize: "10px", fontWeight: 600, fontFamily: "system-ui" }}
              >
                {formatValue(d.value)}
              </text>
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const x = left + i * slotWidth + slotWidth / 2;
          const labelY = top + plotH + 16;
          const maxChars = Math.max(6, Math.floor(slotWidth / 6.5));
          const label = d.label.length > maxChars ? `${d.label.slice(0, maxChars - 1)}…` : d.label;
          return (
            <text key={i} x={x} y={labelY} textAnchor="middle" fill="#94a3b8" style={{ fontSize: "11px", fontWeight: 500, fontFamily: "system-ui" }}>
              {label}
            </text>
          );
        })}

        {/* Baseline (bottom of plot) */}
        {floor >= 0 && (
          <line x1={left} y1={top + plotH} x2={svgWidth - right} y2={top + plotH} stroke="#475569" strokeOpacity="0.2" strokeWidth="1" />
        )}
      </svg>
    </div>
  );
};

// --- Table Chart ---

export const TableChart = ({ data }: { data: ChartDatum[] }) => {
  if (data.length === 0) return null;

  const absMax = Math.max(...data.map(d => Math.abs(d.value)), 1);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">Category</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Value</th>
            <th className="hidden w-1/3 px-4 py-2.5 text-left text-xs font-semibold text-slate-500 sm:table-cell" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.map((item, i) => {
            const isNeg = item.value < 0;
            return (
              <tr key={i} className="transition-colors hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-medium text-slate-700">{item.label}</td>
                <td className={`px-4 py-2.5 text-right font-mono text-sm font-semibold ${isNeg ? "text-red-500" : "text-slate-900"}`}>
                  {formatFullValue(item.value)}
                </td>
                <td className="hidden w-1/3 px-4 py-2.5 sm:table-cell">
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${isNeg ? "bg-gradient-to-r from-red-500 to-red-400" : "bg-gradient-to-r from-blue-500 to-blue-400"}`}
                      style={{ width: `${(Math.abs(item.value) / absMax) * 100}%` }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
